import { computed } from "vue"
import { useAuthStore } from "@/store/authentication"

// Mirrors permissionService.js, but gates on the tenant's subscription entitlements (feature-sets)
// rather than the user's permissions.
//
// Entitlements are read from the auth store (filled by GET /principal), NOT from the subscription
// store (GET /billing/subscription). That endpoint requires the VIEW_BILLING permission. Every
// predefined role is now seeded with it, but a role is a per-tenant document the customer can edit,
// so a user without it is a supported case — and for them /billing/subscription 403s and the
// subscription store keeps an empty entitlement list forever. Feature gating has to work for all
// users, so it must come from an endpoint every authenticated user may call.
//
// So anything that changes what the tenant has bought must re-read /principal — refreshing the
// subscription store gates nothing here. Subscribing gets it free (it ends in a page load);
// `ChangePlanView` and the FEATURE_NOT_ENTITLED recovery in `services/api.js` call `loadPrincipal()`.
//
// Unknown entitlements still mean "allow", not "deny": the list is empty until /principal has been
// read, and hiding a purchased feature during that window is worse than briefly offering one. An
// empty list is exactly that "not known yet" signal — every subscribed tenant is entitled to at
// least one feature-set, and a tenant with none is unsubscribed and already stopped by the paywall
// (402) before it can reach a gated page. The backend's 403 FEATURE_NOT_ENTITLED remains the
// authority either way, exactly as its 402 is for the subscription paywall.
const entitled = featureSet => {
  const { entitlements } = useAuthStore()
  return !entitlements.length || entitlements.includes(featureSet)
}

// Checks if the tenant is entitled to a specific feature-set.
export const hasFeatureSet = featureSet => computed(() => entitled(featureSet))

// Checks if the tenant is entitled to at least one of the given feature-sets.
export const hasAnyFeatureSet = featureSets => computed(() => featureSets.some(entitled))

// Non-reactive helpers for use in guards / plain functions.
export const isEntitled = featureSet => entitled(featureSet)
export const isEntitledToAny = featureSets => featureSets.some(entitled)

// Whether the tenant has [featureSet], or null while that is not known yet. For *telling* somebody
// what their plan includes, where the "allow while unknown" above would turn into a claim that may be
// wrong either way, and for a request that would be refused while it is not known (the Tagesmeldungen
// count, store/daySubmission.ts). Gating what is shown stays with the helpers above.
export const knownEntitlement = featureSet => {
  const { entitlements } = useAuthStore()
  return entitlements.length ? entitlements.includes(featureSet) : null
}

// Whether [featureSet] is unreleased (the backend's `UNRELEASED_FEATURE_SETS`, served on /principal): such a set
// reaches only the workspaces it was granted to; no plan, add-on or exemption carries it, so nothing may
// offer it for sale. False while /principal has not answered, as for an older backend that does not say:
// the billing catalog, which leaves these sets out, is then the only word on it.
export const isUnreleased = featureSet => useAuthStore().unreleasedFeatureSets.includes(featureSet)
